import type { ReactNode } from 'react';

import LoadingSpinner from 'components/LoadingSpinner';
import Message from 'components/Message';
import Placeholder from 'components/Placeholder';
import Text from 'components/Text';
import { useSitecoreContext } from 'hooks';
import type { ComponentRendering } from 'models/sitecore';
import { cn } from 'utils/classNames';
import { getTestDataAttrFrom } from 'utils/helpers';
import { useI18n } from 'utils/i18n';

import LayoutContainer from './LayoutContainer';

interface Props {
	children: ReactNode;
	hasLoadingError?: boolean;
	heading?: ReactNode;
	headingClassName?: string;
	isLoading?: boolean;
	loadingErrorHeading?: string | undefined;
	loadingErrorText?: string;
	mainContentSize?: 'regular' | 'thin';
	rendering: ComponentRendering | undefined;
	sidebarContent?: ReactNode;
}

/** Container for account pages that handles placeholders and the main grid. */
export default function AccountLayoutContainer({
	children,
	hasLoadingError,
	heading,
	headingClassName,
	isLoading,
	loadingErrorHeading,
	loadingErrorText,
	mainContentSize = 'regular',
	sidebarContent,
}: Props) {
	const { t } = useI18n();
	const { sitecoreContext } = useSitecoreContext();

	return (
		<LayoutContainer>
			<div className="mt-r-2xl grid grid-flow-col grid-cols-4 gap-r-md gap-y-0 md:grid-cols-12">
				<div className="col-span-2 row-span-2 block max-md:hidden print:hidden">
					<Placeholder
						name="jula-side-navigation"
						rendering={sitecoreContext.route}
					/>
				</div>

				<div
					className={cn(
						'col-span-full md:col-span-5 md:col-start-4 print:col-start-1!',
						mainContentSize === 'thin' && 'max-w-100',
						sidebarContent && 'print:col-span-8!',
						!sidebarContent && 'print:col-span-full!',
					)}
				>
					{heading && (
						<Text as="h1" className={cn('mb-r-xl', headingClassName)}>
							{heading}
						</Text>
					)}
					{!isLoading && !hasLoadingError && children}
					{hasLoadingError && (
						<Message
							variant="error"
							heading={loadingErrorHeading}
							text={loadingErrorText || t('account_generic_fetch_error_text')}
						/>
					)}
					{isLoading && <LoadingSpinner />}
				</div>

				{sidebarContent && (
					<div
						data-cy={getTestDataAttrFrom('account-sidebar')}
						className="col-span-full md:col-span-3 md:col-start-10 print:col-start-10!"
					>
						{sidebarContent}
					</div>
				)}
			</div>
		</LayoutContainer>
	);
}
AccountLayoutContainer.displayName = 'AccountLayoutContainer';
